import React from 'react';
import { getFormatDateFromObject, getDateFromString } from 'shared-components';
import {
  DocAccessType,
  IDocuments,
  NonUKVisaRefusalMap,
  UKVisaRefusal,
  UKVisaRefusalMap,
  VisaRefusalDocList,
} from '../../../../types';
import { Section } from '../../../Finger/Section';
import FingerWithApprove from '../../../atoms/finger-with-approve';
import { SummaryRow } from '../../../Summary/SummaryRow';
import NoDocuments from '../../../atoms/no-doc-placeholder';
import { getFingerStatus, getVisible, hasDeleted } from '../../../../utilities/documents';
import { UserRole } from '../../../../store/auth/types';
import { useTranslation } from "react-i18next";

interface IProps {
  infoOnly: boolean;
  userAccess?: DocAccessType;
  isNonUKVisaRefusal: boolean;
  documents: IDocuments;
  warning?: string;
  userRole?: UserRole;
  canResume: boolean;
  canUpload: boolean;
  canOpen: boolean;
  canApprove: boolean;
  canReject: boolean;
  isDeleting: boolean;
  canAcknowledge: boolean;
  showRisks: boolean;
  onResume: (url: string) => void;
  onUploadClick: (visaKey: string, docKey: string) => void;
  onOpenClick: (visaKey: string, docKey: string) => void;
  onApproveClick: (visaKey: string, docKey: string) => void;
  onRejectClick: (visaKey: string, docKey: string) => void;
  onRescindRejectClick: (visaKey: string, docKey: string) => void;
  onDeleteClick: (visaKey: string, docKey: string) => void;
  onAcknowledgeClick: (visaKey: string, docKey: string) => void;
}

const VisaRefusal = (props: IProps) => {
    const { t } = useTranslation('common');
  const { showRisks, infoOnly, userAccess, userRole } = props;

  const getVisaTypeLabel = (visaRefusal: UKVisaRefusal) => {
    const visasArray: unknown = Array.from(Object.values(visaRefusal));
    const { visaType } = visasArray[0];
    if (!props.isNonUKVisaRefusal) {
      if (visaType === 'Student visa (Tier 4 General/Student Route)') {
        return 'Student';
      } else if (visaType === 'Child Student visa (Tier 4 Child/Child Student Route)') {
        return 'Child Student';
      }
    }
    return visaType;
  };
  const refusals: UKVisaRefusalMap | NonUKVisaRefusalMap = props.isNonUKVisaRefusal
    ? (props.documents.nonUkVisaRefusals ?? {})
    : (props.documents.visaRefusals ?? {});
  let hasDocs = false;
  let hasDeletedDocs = false;
  const docNodes: React.ReactNode[] = [];

  Object.entries(refusals).forEach(([visasKey, visaRefusal]) => {
    const visibleDocs = getVisible({
      documentsHash: visaRefusal,
      userAccess,
      infoOnly,
      userRole,
    }) as VisaRefusalDocList[];
    const visaTypeLabel = getVisaTypeLabel(visaRefusal);

    if (hasDeleted(visaRefusal)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: VisaRefusalDocList, index: number) => {
          const { key: docKey, item } = doc;
          const visasArray: unknown = Array.from(Object.values(visaRefusal));
          const { country } = visasArray[0];
          const { year } = visasArray[0];
          return (
            <FingerWithApprove
              id={`visa-refusal-${docKey}`}
              key={`visa-refusal-${docKey}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              label={
                props.isNonUKVisaRefusal
                  ? `Non-UK Visa Refusal ${country} - ${year}`
                  : `Visa Refusal ${visaTypeLabel} - ${year} ${
                      Object.keys(visaRefusal).length > 1 ? (index > 0 ? index + 1 : '') : ''
                    }`
              }
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              onUploadClick={() => props.onUploadClick(visasKey, docKey)}
              onOpenClick={() => props.onOpenClick(visasKey, docKey)}
              onApproveClick={() => props.onApproveClick(visasKey, docKey)}
              onRejectClick={() => props.onRejectClick(visasKey, docKey)}
              onRescindRejectClick={() => props.onRescindRejectClick(visasKey, docKey)}
              onDeleteClick={() => props.onDeleteClick(visasKey, docKey)}
              onAcknowledgeClick={() => props.onAcknowledgeClick(visasKey, docKey)}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            >
              <>
                {item.hasVisaRefusalDoc === false ? (
                  <>
                    <SummaryRow
                      key={`has-no-refusal-doc-${index}`}
                      label={t('common:hasRefusalDocument')}
                      fullLabel={'Do you still have a copy of this document available for upload?'}
                      value={`No`}
                      isRisk={showRisks && true}
                    />

                    <SummaryRow
                      key={`has-no-refusal-doc-reason-${index}`}
                      label={t('common:reason')}
                      fullLabel={'Please give a reason why you do not have the document:'}
                      value={item.hasNoVisaRefusalDocReason}
                    />
                  </>
                ) : (
                  item.refusalDate && (
                    <>
                      <SummaryRow
                        key={`refusal-date-${index}`}
                        label={t('common:refusalDate')}
                        fullLabel="What is the date of this refusal? (as per the document)"
                        value={`${getFormatDateFromObject(getDateFromString(item.refusalDate))}`}
                      />
                    </>
                  )
                )}
              </>
            </FingerWithApprove>
          );
        }),
      );
    }
  });

  if (!hasDocs && !hasDeletedDocs) {
    return null;
  }

  if ((infoOnly || userAccess) && !hasDocs) {
    return null;
  }

  return (
    <Section
      label={t('common:youHaveIndicatedYouHaveVisaRefus', { length: Object.keys(refusals).length, UK: props.isNonUKVisaRefusal ? 'non-UK' : '' })}
      canEdit={props.canResume}
      edit={() => {
        props.onResume('history/step-2');
      }}
      warning={props.warning}
    >
      {hasDocs ? docNodes : <NoDocuments />}
    </Section>
  );
};

export default VisaRefusal;
